import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import processLead from 'site-react/core/helpers/LeadsProcessor';
import logError from 'site-react/core/helpers/logError';
import authService from 'site-react/auth/AuthService';

import attemptAddToShortlist from './attemptAddToShortlist';
import attemptRemoveFromShortlist from './attemptRemoveFromShortlist';
import config from 'site-react/config';
import Context from './context';

const localStorageKey = `hubble_${config.ENV}_trackShortlist_shortlistProvider`;

// For more about why this is a context, and contexts in general, see our docs
// on the tech wiki:
// https://github.com/HubbleHQ/tech-wiki/blob/master/frontend/react-components.md#context

function handleUserNotLoggedIn(shortlistId, actionMeta) {
  try {
    window.localStorage.setItem(
      localStorageKey,
      JSON.stringify({ shortlistId, actionMeta }),
    );
  } catch (e) {
    // Fail silently - let the user log in and re-shortlist manually.
    logError("Local storage failed. Listing won't be shortlisted after login.");
  }

  let url = new URL(window.location.href);
  url.searchParams.set('trackShortlist', 'true');

  authService.requestLogin(url.toString());
}

/**
 * Updates the users budget and the processes a lead for the shortlist action.
 */
async function updateUserBudget(
  { updateUser, user },
  { budget, budgetSource = '', ctaPageLocation = '', office },
) {
  try {
    const companySize = office && office.desksAvailable;
    updateUser({
      budget,
      budget_source: budgetSource || 'Shortlist',
      people: companySize || 1,
    });

    // This should never be needed- apart from in tests where we don't add a user.
    // However, i'm putting it here as a safety net, since i don't want to risk breaking
    // everything!!!
    if (!user) return;

    processLead({
      budget,
      company_size: companySize,
      cta_page_location: ctaPageLocation,
      email: user.email,
      event_type: 'shortlist',
      priceplan_id: office.id,
    });
  } catch (err) {
    logError(err);
  }
}

function ShortlistProvider({
  addError,
  children,
  isLoggedIn,
  updateUser,
  user,
}) {
  const [isShortlistLoaded, setIsShortlistLoaded] = useState(false);
  const [shortlist, setShortlist] = useState([]);

  /**
   * Add an office to shortlist, add extra parameters to update a user with
   */
  const addToShortlist = useCallback(
    async (shortlistId, actionMeta = {}) => {
      if (isShortlistLoaded && !isLoggedIn) {
        handleUserNotLoggedIn(shortlistId, actionMeta);
        return;
      }

      if (isShortlistLoaded && !shortlist.includes(shortlistId)) {
        const isAdded = await attemptAddToShortlist(shortlistId, addError);
        if (!isAdded) return;
        updateUserBudget({ updateUser, user }, actionMeta);

        setShortlist([...shortlist, shortlistId]);
      }
    },
    [addError, isLoggedIn, isShortlistLoaded, shortlist, updateUser, user],
  );

  useEffect(() => {
    userUpdated(user);
  }, [user]);

  useEffect(() => {
    /**
     * Post login handles getting the environment variable which has the
     * shortlist information and adding it to the shortlist.
     */
    async function handleShortlistPostLogin() {
      try {
        const { shortlistId, actionMeta } = JSON.parse(
          window.localStorage.getItem(localStorageKey),
        );
        await addToShortlist(parseInt(shortlistId), actionMeta);
        window.localStorage.removeItem(localStorageKey);
      } catch (e) {
        logError('Nothing was shortlisted post login');
      }
    }

    // If any url passes ?trackShortlist={anything here}
    // Track that shortlist!
    const url = new URL(window.location.href);

    if (
      url.searchParams.get('trackShortlist') &&
      isLoggedIn &&
      isShortlistLoaded
    ) {
      // Remove track shortlist url without page refresh
      url.searchParams.delete('trackShortlist');
      window.history.pushState({ path: url.toString() }, '', url.toString());
      // Handle shortlist
      handleShortlistPostLogin();
    }
  }, [isLoggedIn, isShortlistLoaded, addToShortlist]);

  async function removeFromShortlist(shortlistId) {
    if (isShortlistLoaded && shortlist.includes(shortlistId)) {
      const isRemoved = await attemptRemoveFromShortlist(shortlistId, addError);
      if (!isRemoved) return;

      setShortlist(shortlist.filter((id) => id !== shortlistId));
    }
  }

  /**
   * When the user has changed - usually on page load, but also if a user logs
   * in or logs out - we want to load in the latest shortlist from the server.
   */
  function userUpdated(user) {
    setIsShortlistLoaded(true);
    setShortlist(user ? [...user.favourites] : []);
  }

  return (
    <Context.Provider
      value={{
        addToShortlist,
        isShortlistLoaded,
        removeFromShortlist,
        shortlist,
      }}
    >
      {children}
    </Context.Provider>
  );
}

ShortlistProvider.defaultProps = {
  children: null,
  user: null,
};

ShortlistProvider.propTypes = {
  /**
   * A function to send error messages to the ErrorHandler. Normally comes from context.
   */
  addError: PropTypes.func.isRequired,

  /**
   * Child components
   */
  children: PropTypes.node,

  /**
   * Is the user currently logged in? Normally comes from context.
   */
  isLoggedIn: PropTypes.bool.isRequired,

  /**
   * Function to update user data.
   */
  updateUser: PropTypes.func.isRequired,

  /**
   * The current user. Normally comes from context.
   */
  user: PropTypes.shape({
    favourites: PropTypes.arrayOf(PropTypes.number),
  }),
};

export default ShortlistProvider;
